import { useSyncActivity } from "@/lib/sync-activity-context";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useLocation } from "wouter";

/**
 * Pure helper for composing the post-completion banner message. Exported so
 * the OCT-3878 unit tests can pin the observability-suffix logic without a
 * React render. The suffix is adapter-agnostic - any provider that populates
 * `recordsFetched` / `recordsUnchanged` on its sync_log row gets the same
 * shape; pre-OCT-3857 Wafeq rows leave both null and the suffix is omitted.
 *
 * @spec DOMAIN D9 - sync_log columns are the single source for the surface.
 */
export interface BannerSummary {
  adapterName: string;
  recordsProcessed: number;
  recordsFailed: number;
  recordsFetched: number | null;
  recordsUnchanged: number | null;
  hasErrors: boolean;
}

export function composeCompletionMessage(s: BannerSummary): string {
  const successCount = s.recordsProcessed - s.recordsFailed;
  const observabilitySuffix = (s.recordsFetched != null || s.recordsUnchanged != null)
    ? ` (${s.recordsFetched ?? 0} fetched${s.recordsUnchanged != null ? `, ${s.recordsUnchanged} unchanged` : ""})`
    : "";
  if (s.hasErrors && s.recordsFailed > 0) {
    return `Sync complete - ${successCount} record${successCount !== 1 ? "s" : ""} synced, ${s.recordsFailed} need${
      s.recordsFailed !== 1 ? "" : "s"
    } your attention${observabilitySuffix}`;
  }
  if (s.recordsProcessed > 0) {
    return `Your data is up to date - ${s.recordsProcessed} record${
      s.recordsProcessed !== 1 ? "s" : ""
    } synced with ${s.adapterName}${observabilitySuffix}`;
  }
  if (s.recordsFetched != null && s.recordsFetched > 0) {
    // The adapter walked records upstream but everything was already in
    // sync locally. Render "42 fetched, all up to date" instead of the
    // older "everything is up to date" which hid the fact that the
    // adapter actually did work.
    return `Your data is up to date with ${s.adapterName} - ${s.recordsFetched} fetched${
      s.recordsUnchanged != null ? `, ${s.recordsUnchanged} unchanged` : ""
    }`;
  }
  return `Sync complete - everything is up to date with ${s.adapterName}`;
}

export function SyncBanner() {
  const { isSyncActive, activeSyncInfo, syncJustCompleted, dismissCompletion } = useSyncActivity();
  const [, setLocation] = useLocation();

  if (!isSyncActive && !syncJustCompleted) return null;

  const handleClick = () => {
    if (syncJustCompleted) {
      dismissCompletion();
    }
    setLocation("/sync");
  };

  if (isSyncActive) {
    const directionLabel = activeSyncInfo?.direction === "push" ? "pushing to" : "syncing with";
    const adapterLabel = activeSyncInfo?.adapterName || "your accounting software";
    const entityPhase = activeSyncInfo?.currentEntityPhase;
    const phaseLabel = entityPhase ? ` — syncing ${entityPhase}s\u2026` : "";
    const message = `We're ${directionLabel} ${adapterLabel} now${phaseLabel}`;

    return (
      <div
        onClick={handleClick}
        className="relative flex items-center justify-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-foreground/5 border-b border-border cursor-pointer hover:bg-foreground/8 transition-colors"
        data-testid="banner-sync-active"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleClick();
        }}
      >
        <Loader2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground animate-spin" />
        <span className="truncate" data-testid="text-sync-active-message">{message}</span>
        <span className="text-[10px] text-muted-foreground hidden sm:inline">Click to view details</span>
      </div>
    );
  }

  if (syncJustCompleted) {
    const { hasErrors } = syncJustCompleted;
    // OCT-3878 (Wave E): delegate to composeCompletionMessage so the
    // observability-suffix logic is testable in isolation. The helper renders
    // the same "(N fetched, M unchanged)" shape for every adapter that
    // populates the underlying sync_log columns - no per-provider branching.
    const message = composeCompletionMessage(syncJustCompleted);

    return (
      <div
        onClick={handleClick}
        className="relative flex items-center justify-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm bg-healthy/10 border-b border-healthy/20 cursor-pointer hover:bg-healthy/15 transition-colors"
        data-testid="banner-sync-complete"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleClick();
        }}
      >
        {hasErrors
          ? <AlertTriangle className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-degraded" />
          : <CheckCircle2 className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-healthy" />}
        <span className="truncate" data-testid="text-sync-complete-message">{message}</span>
      </div>
    );
  }

  return null;
}
